import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Zap, KeyRound, ExternalLink, CheckCircle2, Plus, ChevronDown, ChevronUp,
  Newspaper, BarChart3, Globe, FileText, Users, Bitcoin, DollarSign,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminMarketProviderCatalog, adminActivateNoAuthProvider, getErrorMessage } from '../../lib/api';

const TIER_TONE = {
  'free-no-auth': 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  'free-key':     'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  'free-oauth':   'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200',
  'paid':         'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
};

const TIER_LABEL = {
  'free-no-auth': '⚡ no key',
  'free-key':     '🔑 free + key',
  'free-oauth':   '🪪 OAuth',
  'paid':         '💰 paid',
};

const CATEGORY_ICON = {
  prices: BarChart3,
  news: Newspaper,
  macro: Globe,
  filings: FileText,
  social: Users,
  crypto: Bitcoin,
  fx: DollarSign,
};

const CATEGORY_LABEL = {
  prices: 'Prezzi & quotazioni',
  news: 'News & sentiment',
  macro: 'Macro & tassi',
  filings: 'Filings SEC',
  social: 'Social & attention',
  crypto: 'Crypto',
  fx: 'Forex',
};

/**
 * Catalogo completo dei provider di mercato.
 *
 * Mostra cards raggruppate per categoria, con badge tier, descrizione, link
 * per ottenere la chiave, e azioni contestuali (Attiva / Inserisci chiave).
 *
 * Props:
 *  - onRequestNewProvider: (kind) => void — apre il dialog di creazione con kind pre-selezionato
 *  - onRefresh: () => void — chiamato dopo activation no-auth per refreshare la lista esistente
 */
const MarketProviderCatalog = ({ onRequestNewProvider, onRefresh }) => {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(null);
  const [expanded, setExpanded] = useState(true);

  const load = () => {
    setLoading(true);
    adminMarketProviderCatalog()
      .then(r => setCatalog(r.data.items || []))
      .catch(e => toast.error(getErrorMessage(e, 'Errore catalogo')))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const groups = useMemo(() => {
    const by = {};
    for (const e of catalog) {
      const c = e.category || 'other';
      if (!by[c]) by[c] = [];
      by[c].push(e);
    }
    // Ordine fisso delle categorie + alpha all'interno
    const order = ['prices', 'news', 'macro', 'filings', 'social', 'crypto', 'fx'];
    return order
      .filter(c => by[c])
      .map(c => ({ category: c, items: by[c].sort((a, b) => {
        // No-auth prima, poi free-key, poi oauth
        const tierOrder = { 'free-no-auth': 0, 'free-key': 1, 'free-oauth': 2, paid: 3 };
        const da = tierOrder[a.tier] ?? 99;
        const db = tierOrder[b.tier] ?? 99;
        return da - db || a.name.localeCompare(b.name);
      })}));
  }, [catalog]);

  const handleActivate = async (entry) => {
    setActivating(entry.kind);
    try {
      const r = await adminActivateNoAuthProvider(entry.kind);
      if (r.data.already_existed) {
        toast.info(`${entry.name} era già attivo`);
      } else {
        toast.success(`${entry.name} attivato`);
      }
      load();
      onRefresh?.();
    } catch (e) {
      toast.error(getErrorMessage(e, `Errore attivazione ${entry.name}`));
    } finally {
      setActivating(null);
    }
  };

  const totalConnected = catalog.filter(e => e.is_connected).length;
  const totalAvailable = catalog.length;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-base flex items-center gap-2">
            🗂 Catalogo Provider
            <span className="text-xs text-muted-foreground font-normal">
              {totalConnected}/{totalAvailable} connessi
            </span>
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)}>
            {expanded ? <><ChevronUp className="w-4 h-4 mr-1" /> Riduci</> : <><ChevronDown className="w-4 h-4 mr-1" /> Espandi</>}
          </Button>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent>
          {loading && <div className="h-40 bg-muted animate-pulse rounded-lg" />}
          {!loading && groups.map(g => {
            const Icon = CATEGORY_ICON[g.category] || BarChart3;
            return (
              <div key={g.category} className="mb-6 last:mb-0">
                <h3 className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2 flex items-center gap-2">
                  <Icon className="w-3.5 h-3.5" />
                  {CATEGORY_LABEL[g.category] || g.category}
                  <Badge variant="outline" className="text-[9px]">{g.items.length}</Badge>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {g.items.map(e => {
                    const isNoAuth = e.tier === 'free-no-auth';
                    const isConnected = e.is_connected;
                    return (
                      <div
                        key={e.kind}
                        className={`rounded-lg border px-4 py-3 transition-colors ${
                          isConnected
                            ? 'border-emerald-500/30 bg-emerald-50/30 dark:bg-emerald-950/10'
                            : 'hover:border-primary/40'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <div className="min-w-0">
                            <p className="font-semibold text-sm flex items-center gap-1.5 flex-wrap">
                              {e.name}
                              {isConnected && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />}
                            </p>
                            <p className="text-[10px] font-mono text-muted-foreground">{e.kind}</p>
                          </div>
                          <Badge className={`text-[9px] shrink-0 ${TIER_TONE[e.tier]}`}>
                            {TIER_LABEL[e.tier]}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground leading-snug mb-2 line-clamp-3">
                          {e.description}
                        </p>
                        <p className="text-[10px] text-muted-foreground mb-2">
                          ⏱ {e.rate_limit}
                          {e.notes && <span className="block mt-0.5 italic">💡 {e.notes}</span>}
                        </p>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {isNoAuth && !isConnected && (
                            <Button
                              size="sm"
                              variant="default"
                              disabled={activating === e.kind}
                              onClick={() => handleActivate(e)}
                              className="h-7 text-xs"
                            >
                              <Zap className="w-3 h-3 mr-1" />
                              {activating === e.kind ? 'Attivo…' : 'Attiva'}
                            </Button>
                          )}
                          {!isNoAuth && e.in_enum && (
                            <Button
                              size="sm"
                              variant={isConnected ? 'outline' : 'default'}
                              onClick={() => onRequestNewProvider?.(e.kind)}
                              className="h-7 text-xs"
                            >
                              <KeyRound className="w-3 h-3 mr-1" />
                              {isConnected ? 'Aggiorna' : 'Inserisci chiave'}
                            </Button>
                          )}
                          {!isNoAuth && !e.in_enum && (
                            <Badge variant="outline" className="text-[9px]">
                              integrazione futura
                            </Badge>
                          )}
                          {e.get_key_url && (
                            <a
                              href={e.get_key_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[10px] text-primary hover:underline flex items-center gap-0.5"
                            >
                              Ottieni chiave <ExternalLink className="w-2.5 h-2.5" />
                            </a>
                          )}
                          {isConnected && (
                            <span className="text-[10px] text-emerald-600 ml-auto">✓ attivo</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </CardContent>
      )}
    </Card>
  );
};

export default MarketProviderCatalog;
